const ZERO_PRICE = Object.freeze({ uah: 0, eur: 0 });

export const PROJECT_CATEGORY_KEYS = Object.freeze({
  residential: "residential",
  commercial: "commercial",
});

const CATEGORY_ALIASES = Object.freeze({
  residential: PROJECT_CATEGORY_KEYS.residential,
  commercial: PROJECT_CATEGORY_KEYS.commercial,
  "житло": PROJECT_CATEGORY_KEYS.residential,
  "комерція": PROJECT_CATEGORY_KEYS.commercial,
  "mieszkalny": PROJECT_CATEGORY_KEYS.residential,
  "komercyjny": PROJECT_CATEGORY_KEYS.commercial,
});

const SHARED_COMPONENT_PRICES = Object.freeze({
  furniture_layout: { uah: 100, eur: 3.75 },
  visualization: { uah: 300, eur: 11.25 },
  custom_furniture_drawings: { uah: 90, eur: 3.38 },
  wall_elevations: { uah: 90, eur: 3.38 },
});

const CATEGORY_CONFIG = Object.freeze({
  [PROJECT_CATEGORY_KEYS.residential]: {
    projectTypes: ["full", "visual", "technical", "constructor"],
    baseRates: {
      full: { uah: 1200, eur: 45 },
      visual: { uah: 840, eur: 38 },
      technical: { uah: 360, eur: 20 },
    },
    componentPrices: {
      ...SHARED_COMPONENT_PRICES,
      single_drawing: { uah: 30, eur: 1.13 },
      specification: { uah: 60, eur: 2.18 },
    },
    constructorDefaults: {
      planningChecked: true,
      planningVariants: 3,
      partialChecked: true,
      specificationChecked: true,
      selectedDrawKeys: "all",
    },
    drawingDefinitions: [
      { key: "montage", priceKey: "single_drawing" },
      { key: "demolition", priceKey: "single_drawing" },
      { key: "plaster_works", priceKey: "single_drawing" },
      { key: "heating_floor", priceKey: "single_drawing" },
      { key: "hvac_go", priceKey: "single_drawing" },
      { key: "sanitary_layout", priceKey: "single_drawing" },
      { key: "lighting_switches", priceKey: "single_drawing" },
      { key: "lighting_dimensional", priceKey: "single_drawing" },
      { key: "outlets_layout", priceKey: "single_drawing" },
      { key: "floor_covering", priceKey: "single_drawing" },
      { key: "ceiling_installation", priceKey: "single_drawing" },
      { key: "furniture_layout_plan", priceKey: "single_drawing" },
      { key: "custom_furniture_drawings", priceKey: "custom_furniture_drawings" },
      { key: "wall_elevations", priceKey: "wall_elevations", isRazvortky: true },
    ],
  },
  [PROJECT_CATEGORY_KEYS.commercial]: {
    projectTypes: ["horeca", "open_space", "boutique", "beauty_salon", "constructor"],
    baseRates: {
      horeca: { uah: 1020, eur: 38.29 },
      open_space: { uah: 809, eur: 30.36 },
      boutique: { uah: 450, eur: 25 },
      beauty_salon: { uah: 839, eur: 31.49 },
    },
    componentPrices: {
      ...SHARED_COMPONENT_PRICES,
      installation_plan: { uah: 30, eur: 1.13 },
      demolition_plan: { uah: 30, eur: 1.13 },
      electrical_plan: { uah: 90, eur: 3.38 },
      plumbing_plan: { uah: 30, eur: 1.13 },
      hvac_networks_plan: { uah: 30, eur: 1.13 },
      isometric_projections: { uah: 30, eur: 1.13 },
    },
    constructorDefaults: {
      planningChecked: true,
      planningVariants: 3,
      partialChecked: true,
      specificationChecked: false,
      selectedDrawKeys: "all",
    },
    drawingDefinitions: [
      { key: "installation_plan", priceKey: "installation_plan" },
      { key: "demolition_plan", priceKey: "demolition_plan" },
      { key: "electrical_plan", priceKey: "electrical_plan" },
      { key: "plumbing_plan", priceKey: "plumbing_plan" },
      { key: "hvac_networks_plan", priceKey: "hvac_networks_plan" },
      { key: "custom_furniture_drawings", priceKey: "custom_furniture_drawings" },
      { key: "isometric_projections", priceKey: "isometric_projections" },
      { key: "wall_elevations", priceKey: "wall_elevations", isRazvortky: true },
    ],
  },
});

export function normalizeProjectCategory(value) {
  const normalized = String(value || "").trim().toLowerCase();
  return CATEGORY_ALIASES[normalized] || PROJECT_CATEGORY_KEYS.residential;
}

export function getCategoryConfig(categoryKey) {
  const normalizedKey = normalizeProjectCategory(categoryKey);
  return CATEGORY_CONFIG[normalizedKey] || CATEGORY_CONFIG[PROJECT_CATEGORY_KEYS.residential];
}

export function getProjectTypeKeys(categoryKey) {
  return getCategoryConfig(categoryKey).projectTypes;
}

export function isValidProjectType(categoryKey, projectType) {
  return getProjectTypeKeys(categoryKey).includes(projectType);
}

export function getDrawingDefinitions(categoryKey) {
  return getCategoryConfig(categoryKey).drawingDefinitions;
}

export function getConstructorDefaults(categoryKey) {
  return getCategoryConfig(categoryKey).constructorDefaults;
}

export function shouldShowSpecification(categoryKey) {
  return normalizeProjectCategory(categoryKey) === PROJECT_CATEGORY_KEYS.residential;
}

export function getPriceByKey(priceKey, categoryKey = PROJECT_CATEGORY_KEYS.residential) {
  const prices = getCategoryConfig(categoryKey).componentPrices;
  return Object.hasOwn(prices, priceKey) ? prices[priceKey] : ZERO_PRICE;
}

export function getBaseRateSet(categoryKey, typeKey) {
  const rates = getCategoryConfig(categoryKey).baseRates;
  return Object.hasOwn(rates, typeKey) ? rates[typeKey] : ZERO_PRICE;
}

export function convertPrice(price, currency, { eurToPlnRate, eurToUsdRate }) {
  if (currency === "uah") return roundTo(price.uah, 2);
  if (currency === "pln") return roundTo(price.eur * eurToPlnRate, 3);
  if (currency === "usd") return roundTo(price.eur * eurToUsdRate, 3);

  return roundTo(price.eur, 2);
}

export function shouldRoundFixedPlanRate(categoryKey, currency) {
  return normalizeProjectCategory(categoryKey) !== PROJECT_CATEGORY_KEYS.commercial || currency !== "uah";
}

export function roundDisplayedRate(rate, currency) {
  const step = currency === "uah" ? 10 : 1;
  return Math.ceil(Number(rate) / step) * step;
}

function roundTo(value, digits) {
  const factor = 10 ** digits;
  return Math.round((Number(value) + Number.EPSILON) * factor) / factor;
}
